/**
 * Teilnehmer-Tutorial
 *
 * Führt durch die Teilnehmerverwaltung:
 * Dashboard → ParticipantsList → ParticipantDetail (Foto)
 *
 * Abgedeckte Prozesse: P15 (Teilnehmerliste), P16 (Detail), P17 (Personenfoto)
 * — siehe docs/PROZESSLANDKARTE.md.
 */

import type { TutorialDefinition, TutorialStep } from './types'
import { useParticipantsStore } from '@/stores/participants.store'
import { useRegistrationsStore } from '@/stores/registrations.store'

/**
 * Ermittelt einen Teilnehmer für den Detail-Schritt.
 *
 * Zwei Quellen, weil je nach Einstiegsweg nur eine davon gefüllt ist: das Dashboard
 * lädt `participantsStore`, die Registrierungen kommen erst beim Öffnen der
 * Teilnehmerliste dazu. Ohne den zweiten Versuch bräche das Tutorial genau dann ab,
 * wenn der Nutzer es nach einem Besuch der Teilnehmerliste startet.
 */
function findParticipantId(): number | undefined {
  const participantsStore = useParticipantsStore()
  const fromCache = [...participantsStore.participants.values()][0]
  if (fromCache) return fromCache.id

  const registrationsStore = useRegistrationsStore()
  return registrationsStore.getAllParticipants[0]?.participantId
}

function createSteps(): TutorialStep[] {
  const participantId = findParticipantId()

  const steps: TutorialStep[] = [
    {
      popover: {
        title: 'Teilnehmer und Fotos',
        description:
          'Diese Anleitung zeigt dir die Teilnehmerliste und wie du zu einem Teilnehmer ein Foto speicherst.',
      },
    },
    {
      navigateTo: '/dashboard',
      element: '[data-tutorial="stat-participants"]',
      popover: {
        title: 'Zur Teilnehmerliste',
        description:
          'Die Karte nennt die Anzahl aller Teilnehmer. Tippe darauf, um die Liste zu öffnen.',
        position: 'bottom',
      },
    },
    {
      navigateTo: '/participants',
      // Nur den ersten Eintrag hervorheben, nicht die ganze Liste: bei über
      // 20 Teilnehmern reicht der Rahmen sonst weit über den Bildschirmrand
      // hinaus und der Seitenkopf überlappt. Gleiche Lösung wie in attendance.ts.
      element: '[data-tutorial="participants-list"] > :first-child',
      stagePadding: 8,
      popover: {
        title: 'Die Teilnehmerliste',
        description:
          'Die Liste enthält die Teilnehmer aus allen deinen Kursen. Wer in mehreren Kursen ist, steht trotzdem nur einmal darin. Das Geschenk-Symbol markiert Teilnehmer, deren Geburtstag höchstens sieben Tage zurückliegt oder in den nächsten sieben Tagen ansteht.',
        position: 'top',
      },
    },
    {
      element: '[data-tutorial="participants-search"]',
      popover: {
        title: 'Teilnehmer suchen',
        description:
          'Über die Lupe suchst du nach einem Namen. Der Suchbegriff bleibt gespeichert, auch wenn du die Seite zwischendurch verlässt.',
        position: 'left',
      },
    },
  ]

  if (participantId !== undefined) {
    steps.push(
      {
        navigateTo: `/participant/${participantId}`,
        element: '[data-tutorial="participant-photo"]',
        stagePadding: 8,
        popover: {
          title: 'Foto speichern',
          description:
            'Tippe auf das Bild. Du kannst ein Foto aufnehmen oder eines aus der Galerie auswählen. Das Foto wird auf dem Server gespeichert und ist danach auf allen deinen Geräten sichtbar.',
          position: 'bottom',
        },
      },
      {
        popover: {
          title: 'Fotos ohne Internet',
          description:
            'Ohne Internetverbindung speichert die App das Foto zunächst auf dem Gerät und lädt es hoch, sobald du wieder online bist. Ein kleines Symbol am Bild zeigt an, dass der Upload noch aussteht.',
        },
      },
      {
        popover: {
          title: 'Fertig',
          description:
            'Unter dem Foto stehen die Kontaktdaten. Tippe auf die Telefonnummer, um anzurufen, oder auf die E-Mail-Adresse, um eine Nachricht zu schreiben.',
        },
      }
    )
  } else {
    steps.push({
      popover: {
        title: 'Noch keine Teilnehmer',
        description:
          'Sobald Teilnehmer in deinen Kursen angemeldet sind, kannst du hier ihre Daten öffnen und ein Foto speichern.',
      },
    })
  }

  return steps
}

export const participantsTutorial: TutorialDefinition = {
  id: 'participants',
  title: 'Teilnehmer und Fotos',
  description: 'Teilnehmerliste öffnen, nach Namen suchen und Fotos speichern.',
  icon: 'pi pi-id-card',
  steps: createSteps,
}
